import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useActor } from '@/auth/pin/phoneSession'
import { newId } from '@/lib/ids'
import { enqueueCommand } from '@/offline'
import { Cta, Footer, ItemRow, Strip, V1Screen, WaitCta } from '@/features/gate/v1/kit'
import { DoneScreen } from '@/features/store/v1/bits'
import { C } from '@/features/gate/v1/style'
import { kg, shortCode, tr } from '@/features/gate/v1/text'
import type { SealSamplePayload } from '../commands'
import '../commands'
import { liveOutputs, runParentIds, type ProcessState } from '../projection'
import { sampleCandidates, type SampleCandidate } from '../model/rules'
import { k } from './icons'
import { freshCodeV1 } from './model'
import {
  Hint,
  LeadBox,
  NextArrow,
  outputLabel,
  PBand,
  PHeader,
  runFarmer,
  SaveError,
  saveFailed,
  tail2,
  TickDot,
} from './ui'
import { PScanner, refusalText, V1_QC_SAMPLE_G, WithSite } from './parts'

/** V1-F6-07 — लैब का नमूना: the packing batch, a scoop from every sack, seal, save. */
export function V1QcSample() {
  return <WithSite>{(state) => <QcSample state={state} />}</WithSite>
}

function QcSample({ state }: { state: ProcessState }) {
  const navigate = useNavigate()
  const actor = useActor()
  const all = sampleCandidates(state)
  const [unitId, setUnitId] = useState<string | null>(all.length === 1 ? all[0]!.unit_id : null)
  const [stage, setStage] = useState<'pick' | 'scoop' | 'seal'>('pick')
  const [seal, setSeal] = useState<{ code: string; entry: 'SCAN' | 'TYPED' } | null>(null)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const home = () => navigate('/m/process')
  const pick = all.find((c) => c.unit_id === unitId) ?? null
  const farmer = pick?.run ? runFarmer(pick.run) : undefined
  const unit = pick ? state.units.get(pick.unit_id) : undefined
  const batch = tail2(unit?.code ?? pick?.unit_id ?? '')
  const name = (c: SampleCandidate) =>
    c.run
      ? outputLabel(state, c.run.step_code, c.output_key)
      : (state.units.get(c.unit_id)?.grade ?? '')

  if (done)
    return (
      <DoneScreen
        title={tr('process.v1.seal.done')}
        lines={tr('process.v1.seal.doneLine', { code: shortCode(done) })}
        chips={[]}
        onHome={home}
      />
    )

  // F6.7-a — the batch (preselected when only one)
  if (stage === 'pick' || !pick) {
    const others = pick?.run
      ? liveOutputs(pick.run)
          .filter((o) => !all.some((c) => c.unit_id === o.unit_id))
          .map((o) => outputLabel(state, pick.run!.step_code, o.stream_key))
      : []
    return (
      <V1Screen
        header={
          <PHeader onBack={home} farmer={farmer} icon="scoop" title={tr('process.v1.qc.title')} />
        }
        footer={
          <Footer>
            {pick ? (
              <Cta label={tr('process.v1.next')} icon="next" onClick={() => setStage('scoop')} />
            ) : (
              <WaitCta>{all.length ? tr('process.v1.qc.wait') : tr('process.v1.qc.none')}</WaitCta>
            )}
          </Footer>
        }
      >
        {all.length === 0 && <Strip icon="ok">{tr('process.v1.qc.none')}</Strip>}
        {all.map((c) => (
          <ItemRow
            key={c.unit_id}
            padding="8px 10px"
            onClick={() => setUnitId(c.unit_id)}
            border={c.unit_id === unitId ? C.ink : C.border}
            borderWidth={c.unit_id === unitId ? 3 : 1.5}
            lead={<LeadBox icon="sack" color={C.ok} />}
            title={tr('process.v1.qc.batch', { name: name(c), count: c.sacks.length })}
            line={tr('process.v1.qc.batchLine', {
              kg: kg(c.net_g),
              code: tail2(state.units.get(c.unit_id)?.code ?? c.unit_id),
            })}
            right={c.unit_id === unitId ? <TickDot size={26} /> : <NextArrow />}
          />
        ))}
        {others.length > 0 && (
          <Hint icon="redtie">
            <b>{tr('process.v1.qc.notThese')}</b>
            <br />
            {[...new Set(others)].join(' · ')}
          </Hint>
        )}
      </V1Screen>
    )
  }

  const band = (
    <PBand tone="good" icon="sack">
      {`${tr('process.v1.check.good')} · ${tr('process.v1.seal.from', { code: batch, count: pick.sacks.length })}`}
    </PBand>
  )

  // F6.7-b — one scoop from every sack (a list, no scan)
  if (stage === 'scoop')
    return (
      <V1Screen
        header={
          <PHeader
            onBack={() => setStage('pick')}
            farmer={farmer}
            icon="scoop"
            title={tr('process.v1.scoop.title')}
          />
        }
        band={band}
        footer={
          <Footer>
            <Cta
              label={tr('process.v1.scoop.done', { count: pick.sacks.length })}
              icon="ok"
              onClick={() => setStage('seal')}
            />
          </Footer>
        }
      >
        <Hint icon="scoop">{tr('process.v1.scoop.bag').replaceAll('**', '')}</Hint>
        {pick.sacks.map((s, i) => (
          <ItemRow
            key={s}
            padding="6px 10px"
            lead={<b style={{ width: 22, fontSize: 18, textAlign: 'center' }}>{i + 1}</b>}
            title={tr('process.v1.tag', { no: shortCode(s) })}
          />
        ))}
      </V1Screen>
    )

  // F6.7-d — fill to the mark, seal, scan the seal
  const save = async () => {
    if (!seal) return
    setSaving(true)
    setErr(null)
    try {
      const sample_id = newId()
      const payload: SealSamplePayload = {
        sample_id,
        kind: 'BATCH_QC',
        subject_type: 'UNIT',
        subject_id: pick.unit_id,
        seal_qr_payload: seal.code,
        entry: seal.entry,
        // V1: filled to the mark on the bag, no scale and no photo (the seal ties it to the batch).
        weight_g: V1_QC_SAMPLE_G,
        photo_id: null,
        purpose: 'PACKING',
      }
      await enqueueCommand({
        id: sample_id,
        type: 'seal_sample',
        payload,
        actorId: actor.user_id,
        parentIds: pick.run ? runParentIds(pick.run) : [],
      })
      setDone(seal.code)
    } catch (e) {
      setErr(saveFailed(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <V1Screen
      header={
        <PHeader
          onBack={() => setStage('scoop')}
          farmer={farmer}
          icon="seal"
          title={tr('process.v1.seal.title')}
        />
      }
      band={band}
      footer={
        <Footer>
          <SaveError text={err} />
          {seal ? (
            <Cta
              label={tr('process.v1.seal.save')}
              icon="ok"
              busy={saving}
              onClick={() => void save()}
            />
          ) : (
            <WaitCta>{tr('process.v1.seal.hint')}</WaitCta>
          )}
        </Footer>
      }
    >
      <Hint icon="scoop">{tr('process.v1.seal.mix').replaceAll('**', '')}</Hint>
      {seal ? (
        <ItemRow
          padding="8px 10px"
          lead={<LeadBox icon="seal" color={C.ok} />}
          title={tr('process.v1.seal.scanned', { code: shortCode(seal.code) })}
          right={
            <button
              type="button"
              onClick={() => setSeal(null)}
              style={{
                border: `1.5px solid ${C.border}`,
                background: C.surface,
                borderRadius: 10,
                padding: '8px 10px',
                fontWeight: 700,
              }}
            >
              {tr('process.v1.seal.bad')}
            </button>
          }
        />
      ) : (
        <PScanner
          state={state}
          kind="QC_SEAL"
          icon={k('seal')}
          hint={tr('process.v1.seal.hint')}
          onCode={(code, entry) => {
            const v = freshCodeV1(state, code, 'QC_SEAL', actor.user_id)
            if (!v.ok) return refusalText(v.reason)
            setSeal({ code: v.value, entry })
            return null
          }}
        />
      )}
    </V1Screen>
  )
}
